import React, { useEffect, useRef, useState } from 'react';
import './TestimonialsSection.css';
import ChatAnimation from './ChatAnimation';
import ChatAnimationAlt from './ChatAnimationAlt';

const TestimonialsSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [openTile, setOpenTile] = useState<string | null>('tile-1');
  const [activeChat, setActiveChat] = useState<'database' | 'claims'>('database');
  const [chatToken, setChatToken] = useState(0);

  const toggleTile = (tileId: string) => {
    setOpenTile(openTile === tileId ? null : tileId);
  };

  // Funções para controlar o carrossel de chats
  const switchToDatabase = () => {
    setActiveChat('database');
    setChatToken(prev => prev + 1);
  };

  const switchToClaims = () => {
    setActiveChat('claims');
    setChatToken(prev => prev + 1);
  };

  // Auto-avanço do carrossel
  useEffect(() => {
    const handleChatComplete = (event: CustomEvent) => {
      if (event.detail.id === 'chat' && activeChat === 'database') {
        // Quando o chat de base de dados termina, avança para sinistros
        setTimeout(() => {
          switchToClaims();
        }, 2000); // 2 segundos de pausa
      } else if (event.detail.id === 'chat-alt' && activeChat === 'claims') {
        // Quando o chat de sinistros termina, volta para base de dados
        setTimeout(() => {
          switchToDatabase();
        }, 2000); // 2 segundos de pausa
      }
    };

    window.addEventListener('featureSlideComplete', handleChatComplete as EventListener);
    return () => window.removeEventListener('featureSlideComplete', handleChatComplete as EventListener);
  }, [activeChat]);

  const initializeGridEffects = () => {
    // Primeira grid
    const gridContainer1 = document.getElementById('gridContainer1');
    const centerCard1 = document.getElementById('centerCard');
    const flipCard1 = document.getElementById('flipCard');
    const cardInner1 = document.getElementById('cardInner');
    const allCards1 = document.querySelectorAll('#gridContainer1 .grid-card:not(#centerCard)');


    // Função para inicializar uma grid
    const initializeSingleGrid = (gridContainer: HTMLElement | null, centerCard: HTMLElement | null, flipCard: HTMLElement | null, cardInner: HTMLElement | null, allCards: NodeListOf<Element>) => {
      if (!gridContainer || !centerCard) return;

      let isExpanded = false;
      let isFlipping = false;
      let isFlipped = false;

      // Set initial positions for all cards except center
      allCards.forEach((card) => {
        const element = card as HTMLElement;
        if (element.id !== 'centerCard' && element.id !== 'centerCard2') {
          element.style.opacity = '0';
          element.style.transform = 'scale(0.6)';
          element.style.visibility = 'hidden';
        }
      });

    // Center card is always visible
    const centerElement = centerCard as HTMLElement;
    centerElement.style.opacity = '1';
    centerElement.style.transform = 'scale(1)';
    centerElement.style.visibility = 'visible';

    const expandGrid = () => {
      if (!isExpanded) {
        isExpanded = true;

        // Animate cards to their grid positions with delays
        allCards.forEach((card, index) => {
          const element = card as HTMLElement;
          const delay = (index + 1) * 0.05;

          // Skip center cards
          if (element.id === 'centerCard' || element.id === 'centerCard2') {
            return;
          }

          setTimeout(() => {
            // Simplificar para usar grid CSS
            element.style.opacity = '1';
            element.style.visibility = 'visible';
            element.style.transform = 'scale(1)';
            element.style.transition = 'all 0.6s cubic-bezier(0.86, 0, 0.07, 1)';
          }, delay * 1000);
        });

        // Zoom center card
        const centerElement = centerCard as HTMLElement;
        centerElement.style.transition = 'transform 0.6s cubic-bezier(0.86, 0, 0.07, 1)';
        centerElement.style.transform = 'scale(1.08)';
      }
    };

    const collapseGrid = () => {
      if (isExpanded) {
        isExpanded = false;

        allCards.forEach((card) => {
          const element = card as HTMLElement;
          // Skip center cards
          if (element.id === 'centerCard' || element.id === 'centerCard2') {
            return;
          }
          element.style.transition = 'all 0.6s cubic-bezier(0.86, 0, 0.07, 1)';
          element.style.transform = 'scale(0.6)';
          element.style.opacity = '0';
          element.style.visibility = 'hidden';
        });

        const centerElement = centerCard as HTMLElement;
        centerElement.style.transform = 'scale(1)';

        if (isFlipped && cardInner) {
          const cardInnerEl = cardInner as HTMLElement;
          cardInnerEl.style.transform = 'rotateY(0deg)';
          isFlipped = false;
          isFlipping = false;
        }
      }
    };

    centerCard.removeEventListener('mouseenter', expandGrid);
    gridContainer.removeEventListener('mouseleave', collapseGrid);

    centerCard.addEventListener('mouseenter', expandGrid);
    gridContainer.addEventListener('mouseleave', collapseGrid);

    if (flipCard && cardInner) {
      flipCard.removeEventListener('mouseenter', () => {});
      flipCard.removeEventListener('mouseleave', () => {});

      flipCard.addEventListener('mouseenter', () => {
        if (isExpanded && !isFlipping && !isFlipped) {
          isFlipping = true;
          const cardInnerEl = cardInner as HTMLElement;
          cardInnerEl.style.transition = 'transform 0.8s ease';
          cardInnerEl.style.transform = 'rotateY(180deg)';
          setTimeout(() => {
            isFlipping = false;
            isFlipped = true;
          }, 800);
        }
      });

      flipCard.addEventListener('mouseleave', () => {
        if (isExpanded && (isFlipping || isFlipped)) {
          isFlipping = true;
          const cardInnerEl = cardInner as HTMLElement;
          cardInnerEl.style.transition = 'transform 0.8s ease';
          cardInnerEl.style.transform = 'rotateY(0deg)';
          setTimeout(() => {
            isFlipping = false;
            isFlipped = false;
          }, 800);
        }
      });
    }
    };

    // Inicializar a grid
    initializeSingleGrid(gridContainer1, centerCard1, flipCard1, cardInner1, allCards1);
  };

  const initializeElectricBorder = () => {
    const canvas = document.getElementById('fx') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d')!;
    const dpr = Math.min(2, window.devicePixelRatio || 1);

    let target: HTMLElement | null = null;

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    window.addEventListener('resize', resize);
    resize();

    const bolts = document.querySelectorAll('.bolt');
    bolts.forEach(el => {
      el.addEventListener('pointerenter', () => { target = el as HTMLElement; });
      el.addEventListener('pointerleave', () => { if (target === el) target = null; });
    });

    const boltPath = (x1: number, y1: number, x2: number, y2: number, seg: number, amp: number, t: number) => {
      const pts: { x: number; y: number }[] = [];
      const dx = x2 - x1, dy = y2 - y1;
      const L = Math.hypot(dx, dy) || 1;
      const nx = -dy / L, ny = dx / L;

      for (let i = 0; i <= seg; i++) {
        const p = i / seg;
        const bx = x1 + dx * p, by = y1 + dy * p;
        const wob = (Math.sin(i * 3.1 + t * 8) + Math.cos(i * 5.3 + t * 5)) * 0.5;
        const off = wob * amp * (1 - Math.abs(0.5 - p) * 1.1);
        pts.push({ x: bx + nx * off, y: by + ny * off });
      }
      return pts;
    };

    const stroke = (pts: { x: number; y: number }[], w: number, clr: string, alpha: number, glow?: number) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.lineWidth = w;
      ctx.strokeStyle = clr;
      if (glow) {
        ctx.shadowBlur = glow;
        ctx.shadowColor = clr;
      }
      ctx.beginPath();
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.restore();
    };

    const frame = (ts: number) => {
      const t = ts * 0.001;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (target) {
        const r = target.getBoundingClientRect();
        const pad = 4;
        const x = r.left - pad, y = r.top - pad;
        const w = r.width + pad * 2, h = r.height + pad * 2;

        const top = boltPath(x, y, x + w, y, 28, 5, t + 0.0);
        const right = boltPath(x + w, y, x + w, y + h, 28, 5, t + 0.9);
        const bottom = boltPath(x + w, y + h, x, y + h, 28, 5, t + 1.8);
        const left = boltPath(x, y + h, x, y, 28, 5, t + 2.7);

        const purple = 'rgba(108,70,255,0.9)';

        stroke(top, 3, purple, 0.95, 14);
        stroke(right, 3, purple, 0.95, 14);
        stroke(bottom, 3, purple, 0.95, 14);
        stroke(left, 3, purple, 0.95, 14);

        stroke(top, 1.4, 'rgba(255,255,255,0.9)', 0.95, 0);
        stroke(right, 1.4, 'rgba(255,255,255,0.9)', 0.95, 0);
        stroke(bottom, 1.4, 'rgba(255,255,255,0.9)', 0.95, 0);
        stroke(left, 1.4, 'rgba(255,255,255,0.9)', 0.95, 0);
      }

      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  };

  useEffect(() => {
    // Initialize grid effects
    initializeGridEffects();
    // Removed initializeElectricBorder since we removed the canvas
  }, []);

  // Intersection Observer para animação de scroll
  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.2,
      rootMargin: '0px 0px -50px 0px'
    });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section ref={sectionRef} id="parceiros" className="partners-section">

               {/* Layout dividido 50/50 */}
               <div className="split-layout">
                 {/* Lado esquerdo - Título */}
                 <div className="title-section">
                   <h2 className="main-title">Inteligência Artificial <br/> Para Seguros</h2>
                   <p className="main-subtitle">
                     Veja como o Crafteer transforma a gestão de seguros com IA avançada,
                     análise de dados em tempo real e automação inteligente.
                   </p>

                   {/* Botões de navegação do carrossel */}
                   <div className="chat-buttons">
                     <button
                       className={`chat-btn ${activeChat === 'database' ? 'active' : ''}`}
                       onClick={switchToDatabase}
                     >
                       <span className="btn-icon">
                         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                           <rect x="3" y="3" width="18" height="18" rx="2"/>
                           <path d="M9 9h6v6H9z"/>
                           <path d="M9 1v6M15 1v6M9 17v6M15 17v6M1 9h6M17 9h6M1 15h6M17 15h6"/>
                         </svg>
                       </span>
                       Base de Dados/Simulação
                     </button>

                     <button
                       className={`chat-btn ${activeChat === 'claims' ? 'active' : ''}`}
                       onClick={switchToClaims}
                     >
                       <span className="btn-icon">
                         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                           <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                           <polyline points="14,2 14,8 20,8"/>
                           <line x1="16" y1="13" x2="8" y2="13"/>
                           <line x1="16" y1="17" x2="8" y2="17"/>
                           <polyline points="10,9 9,9 8,9"/>
                         </svg>
                       </span>
                       Sinistros
                     </button>
                   </div>
                 </div>

                 {/* Lado direito - Chat Animation */}
                 <div className="chat-container">
                   {activeChat === 'database' ? (
                     <ChatAnimation
                       key={`database-${chatToken}`}
                       loop={false}
                       botSpeed={3}
                       onComplete={() => {
                         // O auto-avanço é controlado pelo useEffect
                       }}
                     />
                   ) : (
                     <ChatAnimationAlt
                       key={`claims-${chatToken}`}
                       loop={false}
                       botSpeed={3}
                       onComplete={() => {
                         // O auto-avanço é controlado pelo useEffect
                       }}
                     />
                   )}
                 </div>
               </div>
             </section>

  </>
  );
};

export default TestimonialsSection;